import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  isToday,
  format,
} from "date-fns";
import { de as deLocale, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { TaskItem } from "@/components/todo/TaskItem";
import { useApp } from "@/context/AppContext";
import { getPriority } from "@/lib/translations";

export const CalendarView = ({ tasks, onToggle, onDelete, onEdit, onUpdate, onStartTimer }) => {
  const { t, lang } = useApp();
  const locale = lang === "de" ? deLocale : enUS;
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(new Date());

  // Aufgaben nach Fälligkeitstag gruppieren
  const dayTasks = (day) =>
    tasks.filter((tk) => tk.due_date && isSameDay(new Date(tk.due_date), day));

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const weekdayLabels = useMemo(() => {
    const base = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) =>
      format(new Date(base.getTime() + i * 86400000), "EEEEEE", { locale })
    );
  }, [locale]);

  const selectedTasks = dayTasks(selected);

  return (
    <div>
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="calendar-view">
        {/* Kopf: Monat + Navigation */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold capitalize">
            {format(cursor, "MMMM yyyy", { locale })}
          </h2>
          <div className="flex items-center gap-1">
            <button
              data-testid="calendar-prev"
              onClick={() => setCursor((c) => subMonths(c, 1))}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="calendar-today"
              onClick={() => {
                const now = new Date();
                setCursor(now);
                setSelected(now);
              }}
              className="h-9 px-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              {t("today")}
            </button>
            <button
              data-testid="calendar-next"
              onClick={() => setCursor((c) => addMonths(c, 1))}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Wochentage */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekdayLabels.map((w, i) => (
            <div key={i} className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground py-1">
              {w}
            </div>
          ))}
        </div>

        {/* Tage */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const items = dayTasks(day);
            const inMonth = isSameMonth(day, cursor);
            const isSel = isSameDay(day, selected);
            const today = isToday(day);
            return (
              <button
                key={day.toISOString()}
                data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                onClick={() => setSelected(day)}
                className={`relative aspect-square rounded-xl flex flex-col items-center justify-center gap-1 text-sm transition-all ${
                  isSel
                    ? "bg-primary text-primary-foreground font-bold"
                    : today
                    ? "bg-primary/10 text-primary font-semibold"
                    : inMonth
                    ? "text-foreground hover:bg-secondary"
                    : "text-muted-foreground/40 hover:bg-secondary/50"
                }`}
              >
                <span>{format(day, "d")}</span>
                {items.length > 0 && (
                  <div className="flex items-center gap-0.5">
                    {items.slice(0, 3).map((it) => (
                      <span
                        key={it.id}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{
                          backgroundColor: isSel
                            ? "rgba(255,255,255,0.9)"
                            : it.completed
                            ? "hsl(var(--muted-foreground))"
                            : getPriority(it.priority).color,
                        }}
                      />
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Aufgaben des ausgewählten Tages */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-muted-foreground">
          <CalendarDays className="w-4 h-4" />
          <span className="capitalize" data-testid="calendar-selected-label">
            {format(selected, "EEEE, dd. MMMM yyyy", { locale })}
          </span>
        </div>
        {selectedTasks.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-10" data-testid="calendar-empty">
            {t("noTasksThisDay")}
          </div>
        ) : (
          <ul className="flex flex-col gap-3" data-testid="calendar-day-tasks">
            <AnimatePresence mode="popLayout">
              {selectedTasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={onToggle}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onUpdate={onUpdate}
                  onStartTimer={onStartTimer}
                />
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
};
